import * as React from "react"
import { Link, useParams } from "react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "@/lib/api"
import type { Cohort, CohortKey, RunPassResult } from "@/lib/api"
import { SectionCard } from "@/components/console/SectionCard"
import { PageSkeleton, FetchError } from "@/components/console/Fallbacks"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

const COHORT_LABEL: Record<CohortKey, string> = {
  due_for_review: "due for review",
  low_confidence: "low confidence",
  unsourced: "unsourced (legacy)",
  retired_without_successor: "retired without successor",
  duplicate_candidates: "duplicate candidates",
  premise_changed: "premise changed (needs rewrite)",
}

export function Maintenance({ token }: { token: string }) {
  const { audience = "" } = useParams()
  const qc = useQueryClient()
  const [actionError, setActionError] = React.useState<string | null>(null)
  const [revertFailures, setRevertFailures] = React.useState<
    Array<{ id: string; reason: string }> | null
  >(null)

  const report = useQuery({
    queryKey: ["maintenance", audience, token],
    // Let the backend's DEFAULT_COHORT_THRESHOLD govern which cohorts surface.
    queryFn: () => api.maintenance(token, audience),
  })
  const audiences = useQuery({
    queryKey: ["audiences", token],
    queryFn: () => api.audiences(token),
  })

  const invalidate = () => {
    setActionError(null)
    setRevertFailures(null)
    qc.invalidateQueries({ queryKey: ["maintenance", audience] })
  }
  const onError = (error: Error) => setActionError(error.message)

  const sweep = useMutation({
    mutationFn: () => api.reverifySweep(token, audience),
    onSuccess: invalidate,
    onError,
  })
  const runPass = useMutation({
    mutationFn: () => api.runPass(token, audience),
    onSuccess: invalidate,
    onError,
  })
  const verify = useMutation({
    // The item carries no ETag, so read the record for its current ETag first.
    mutationFn: async (id: string) => {
      const current = await api.record(token, audience, id)
      return api.verifyRecord(token, audience, id, current.etag)
    },
    onSuccess: invalidate,
    onError,
  })
  const revert = useMutation({
    mutationFn: (actionId: string) => api.revert(token, audience, actionId),
    onSuccess: (result) => {
      // If there are failures, display them; otherwise fully invalidate
      if (result.failed && result.failed.length > 0) {
        setRevertFailures(result.failed)
        // Still refresh maintenance report to show updated action log
        qc.invalidateQueries({ queryKey: ["maintenance", audience] })
      } else {
        invalidate()
      }
    },
    onError,
  })

  if (report.isPending || audiences.isPending) return <PageSkeleton />
  if (report.isError)
    return <FetchError error={report.error} retry={() => report.refetch()} />
  if (audiences.isError)
    return <FetchError error={audiences.error} retry={() => audiences.refetch()} />

  const canWrite = audiences.data.find((a) => a.id === audience)?.can_write ?? false
  const cohortByKey = new Map(report.data.cohorts.map((c) => [c.key, c]))
  const dueForReview = cohortByKey.get("due_for_review")
  const busy = sweep.isPending || verify.isPending || revert.isPending || runPass.isPending

  return (
    <div className="space-y-3">
      <SectionCard
        title={`${audience} — maintenance`}
        error={actionError || (revertFailures ? `revert partial failure: ${revertFailures.length} target(s) failed` : null)}
        right={
          canWrite && dueForReview && dueForReview.count > 0 ? (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => runPass.mutate()}
              >
                run pass
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => sweep.mutate()}
              >
                re-verify sweep ({dueForReview.count})
              </Button>
            </div>
          ) : canWrite ? (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => runPass.mutate()}
            >
              run pass
            </Button>
          ) : (
            <Link
              to={`/audiences/${encodeURIComponent(audience)}`}
              className="font-mono text-xs text-primary hover:underline"
            >
              records
            </Link>
          )
        }
      >
        {sweep.data ? (
          <p className="mb-2 font-mono text-xs text-muted-foreground">
            swept {sweep.data.considered} — verified {sweep.data.verified.length},
            failed {sweep.data.failed.length}
          </p>
        ) : null}
        {runPass.data ? <RunPassSummary result={runPass.data} /> : null}
        <p className="font-mono text-[10px] text-muted-foreground">
          threshold {report.data.threshold} — cohorts crossing it are marked
          surfaced
        </p>
      </SectionCard>

      {report.data.cohorts.map((cohort) => (
        <CohortCard
          key={cohort.key}
          cohort={cohort}
          audience={audience}
          canWrite={canWrite}
          verifiable={cohort.key === "due_for_review"}
          busy={busy}
          onVerify={(id) => verify.mutate(id)}
        />
      ))}

      {report.data.janitor_actions ? (
        <JanitorActionCard
          janitorActions={report.data.janitor_actions}
          canWrite={canWrite}
          busy={busy}
          onRevert={(actionId) => revert.mutate(actionId)}
          revertFailures={revertFailures}
        />
      ) : null}
    </div>
  )
}

// A pass comes back in one of two shapes depending on whether a reasoning
// model is configured (ADR 0009 J1), so render whichever arrived rather than
// assuming the model-free one.
function RunPassSummary({ result }: { result: RunPassResult }) {
  if (result.type === "reverify_sweep") {
    return (
      <p className="mb-2 font-mono text-xs text-muted-foreground">
        pass run without a model — considered {result.considered}, verified{" "}
        {result.verified.length}, failed {result.failed.length}
      </p>
    )
  }
  const phases = Object.entries(result.phases)
  const applied = phases.reduce((total, [, phase]) => total + phase.applied.length, 0)
  return (
    <div className="mb-2 space-y-0.5">
      <p className="font-mono text-xs text-muted-foreground">
        {applied === 0
          ? "pass run — nothing to apply"
          : `pass run — ${applied} action${applied === 1 ? "" : "s"} applied, each logged below and revertible`}
      </p>
      {phases.map(([name, phase]) => (
        <p key={name} className="font-mono text-[10px] text-muted-foreground">
          {name} — applied {phase.applied.length}, paused {phase.paused.length}, failed{" "}
          {phase.failures.length}
        </p>
      ))}
    </div>
  )
}

function CohortCard({
  cohort,
  audience,
  canWrite,
  verifiable,
  busy,
  onVerify,
}: {
  cohort: Cohort
  audience: string
  canWrite: boolean
  verifiable: boolean
  busy: boolean
  onVerify: (id: string) => void
}) {
  return (
    <SectionCard
      title={COHORT_LABEL[cohort.key]}
      right={
        <span className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
          {cohort.count}
          {cohort.surfaced ? <Badge>surfaced</Badge> : null}
        </span>
      }
    >
      {cohort.items.length === 0 ? (
        <p className="font-mono text-xs text-muted-foreground">Nothing here.</p>
      ) : (
        <ul className="space-y-1">
          {cohort.items.map((item) => (
            <li key={item.id} className="flex items-center justify-between gap-2">
              <Link
                to={`/records/${encodeURIComponent(audience)}/${item.id}`}
                className="min-w-0 truncate font-mono text-xs text-foreground hover:underline"
              >
                {item.title}
              </Link>
              <span className="flex shrink-0 items-center gap-2 font-mono text-[10px] text-muted-foreground">
                <span>conf {item.confidence.toFixed(2)}</span>
                {canWrite && verifiable ? (
                  <Button
                    size="xs"
                    variant="outline"
                    disabled={busy}
                    onClick={() => onVerify(item.id)}
                  >
                    verify
                  </Button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  )
}

function JanitorActionCard({
  janitorActions,
  canWrite,
  busy,
  onRevert,
  revertFailures,
}: {
  janitorActions: import("@/lib/api").JanitorActionCohort
  canWrite: boolean
  busy: boolean
  onRevert: (actionId: string) => void
  revertFailures?: Array<{ id: string; reason: string }> | null
}) {
  return (
    <SectionCard
      title="janitor actions (audit log)"
      right={
        <span className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
          {janitorActions.count}
          {janitorActions.surfaced ? <Badge>surfaced</Badge> : null}
        </span>
      }
    >
      {janitorActions.items.length === 0 ? (
        <p className="font-mono text-xs text-muted-foreground">Nothing here.</p>
      ) : (
        <>
          {revertFailures ? (
            <div className="mb-3 rounded border border-destructive/50 bg-destructive/10 p-2">
              <p className="mb-2 font-mono text-[10px] font-medium text-destructive">
                Revert partial failure: {revertFailures.length} target(s) failed
              </p>
              <ul className="space-y-1">
                {revertFailures.map((failure) => (
                  <li key={failure.id} className="font-mono text-[10px] text-destructive/90">
                    <span className="font-medium">{failure.id}:</span> {failure.reason}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <ul className="space-y-2">
            {janitorActions.items.map((action) => (
              <li
                key={action.action_id}
                className="flex flex-col gap-1 border-l-2 border-muted pl-2 py-1"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs font-medium">
                    {action.phase}: {action.action}
                  </span>
                  {canWrite ? (
                    <Button
                      size="xs"
                      variant="outline"
                      disabled={busy}
                      onClick={() => onRevert(action.action_id)}
                    >
                      revert
                    </Button>
                  ) : null}
                </div>
                <p className="font-mono text-[10px] text-muted-foreground">
                  {action.rationale}
                </p>
                <div className="flex items-center gap-2 font-mono text-[10px] text-muted-foreground">
                  <span>by {action.performed_by}</span>
                  <span>•</span>
                  <span>{new Date(action.performed_at).toLocaleString()}</span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </SectionCard>
  )
}
